import { ChangeDetectorRef, Directive, DoCheck, inject, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import once from 'lodash-es/once';
import { Subscription } from 'rxjs';
import { App } from '@df/core/app';
import { Events } from '@df/core/service/event';
import { CheckoutStepComponentDef } from '@df/checkout/checkout-step.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { MultisiteService } from '@df/core/service/multisite';
import compact from 'lodash-es/compact';
import { Status } from '@df/core/status';
import { NgInject } from '@df/ng/ng-inject';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';

@Directive()
export abstract class AbstractCheckoutPayment<T extends Checkout.IPaymentMethod = Checkout.IPaymentMethod>
    implements DoCheck, OnInit, OnDestroy
{
    /**
     * Script load status
     * @type {Status}
     */
    scriptStatus = new Status();
    /**
     * Payment method code related to component
     * @type {string}
     */
    protected abstract readonly methodCode: string;

    /**
     * Version of 'initialize' method which is invoked only once
     */
    protected init = once(() => this.initialize());

    protected subscriptions = new Subscription();

    readonly events = inject(Events);

    readonly app = inject(App);

    readonly multisite = inject(MultisiteService);

    private _isSelected = false;
    private viewInitiated = false;

    constructor(
        @Inject(Checkout.CheckoutFormPaymentComponentToken) protected checkoutFormComponent: Checkout.ICheckoutForPaymentComponent,
        protected changeDetectorRef: ChangeDetectorRef,
        protected scrollToService: ScrollToService,
        @Optional() protected checkoutStepComponent?: CheckoutStepComponentDef
    ) {}

    ngOnInit() {
        // by default we are in busy state, we will drop this after loading everythin (when its needed)
        this.scriptStatus.busy = true;
        this.subscriptions.add(
            this.events.on(Checkout.EVENTS.SubmitBefore).subscribe((submitBeforeEventData: Checkout.ISubmitBeforeEventData) => {
                // do nothing for other payment methods
                if (this.isSelected()) {
                    this.onCheckoutSubmit(submitBeforeEventData);
                }
            })
        );
        this.subscriptions.add(this.checkoutSubmitStatus.subscribe(() => this.detectChanges()));
    }

    ngAfterViewInit() {
        this.viewInitiated = true;
        if (this.preload()) {
            this.init();
        }
    }

    ngDoCheck() {
        // when method gets selected initiate it (load script etc)
        const isSelected = this.isSelected();
        if (isSelected !== this._isSelected) {
            this._isSelected = isSelected;
            this.detectChanges();
        }
        if (isSelected && this.viewInitiated) {
            this.init();
        }
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    get checkoutSubmitStatus(): Status {
        return this.checkoutFormComponent.status;
    }

    /**
     * Returns required scripts for payment method
     * @returns {Promise<undefined>[]}
     */
    protected abstract getJsAssets(): string[];

    /**
     * Returns required css for payment method
     * @returns {Promise<undefined>[]}
     */
    protected getCssAssets(): string[] {
        return [];
    }

    /**
     * Loads required assets for payment method
     */
    protected loadScripts(): void {
        const jsAssets = compact(this.getJsAssets());
        const cssAssets = compact(this.getCssAssets());
        if (jsAssets.length || cssAssets.length) {
            Promise.all([
                ...jsAssets.map(jsAsset => this.app.request.loadScriptAsync(jsAsset)),
                ...cssAssets.map(cssAsset => this.app.request.loadCssAsync(cssAsset))
            ])
                .then(() => this.onScriptLoadSuccess())
                .catch(err => {
                    console.log(err);
                    this.onScriptLoadError();
                });
        } else {
            this.onScriptLoadSuccess();
        }
    }

    /**
     * Checks if method is currently selected one
     * @return {boolean}
     */
    isSelected(): boolean {
        return this.methodCode === this.checkoutFormComponent.paymentMethodCode;
    }

    /**
     * Returns if we want to preload scripts and initiate form on component init
     * @returns {boolean}
     */
    protected preload(): boolean {
        return false;
    }

    /**
     * Gets payment method object by its code
     * @return {T}
     */
    protected get method(): T | undefined {
        return this.getMethod();
    }

    protected getMethod(): T | undefined {
        return this.checkoutFormComponent.paymentMethodByCode(this.methodCode) as T | undefined;
    }

    /**
     * Checks if component is ready to use
     * @return {boolean}
     */
    protected canUse(): boolean {
        return !this.scriptStatus.busy && !this.scriptStatus.error;
    }

    /**
     * Initiates payment method
     */
    protected initialize(): void {
        this.loadScripts();
    }

    /**
     * On script load success callback
     */
    protected onScriptLoadSuccess(): void {
        this.scriptStatus.success = 'SUCCESS';
    }

    /**
     * On script load error callback
     */
    protected onScriptLoadError(): void {
        this.scriptStatus.error = this.getScriptError();
    }

    /**
     * Returns script error message
     * @returns {string}
     */
    protected getScriptError(): string {
        return 'Loading external script failed. Please use another payment method.';
    }

    /**
     * Abstract method letting you plug in your action before checkout submission
     * @returns {boolean | void}
     */
    protected onCheckoutSubmit(checkoutSubmiBeforeEventData: Checkout.ISubmitBeforeEventData): false | void {
        if (this.checkoutSubmitStatus.busy) {
            checkoutSubmiBeforeEventData.continueSubmission = false;
            return false;
        }
        if (!this.canUse()) {
            checkoutSubmiBeforeEventData.continueSubmission = false;
            this.checkoutSubmitStatus.error = this.scriptStatus.error ? this.getScriptError() : 'Payment method form is not ready yet';
            return false;
        }
    }

    protected detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }

    /**
     * Assures that step is visible and scrolls to container (if given)
     */
    protected scrollTo(container: HTMLElement | undefined) {
        let delay = 0;
        if (!this.checkoutStepComponent?.isCurrent) {
            this.checkoutStepComponent?.goTo();
            delay = 1000;
        }
        if (container) {
            const cb = () => this.scrollToService.scrollTo(container, { offset: 80 });
            delay ? setTimeout(cb, delay) : cb();
        }
    }
}
